import { isDefined } from '@sgme/fp';
import { Button, Icon, VStack } from '@sgme/ui';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import { removeTile } from '@/store/tiles/slice.ts';
import type { IntlMessageId } from '@/types/intl.js';

export type ErrorTileProps = {
  tileId: string;
  onReset?: () => void;

  // 'business' error
  // to display the modal, provide errorCode at least
  errorCode?: string;
  errorTitle?: IntlMessageId;
  errorDescription?: IntlMessageId;
  quoteId?: string;
};

export const ErrorTile = (props: ErrorTileProps) => {
  const { tileId, onReset, errorCode, quoteId, errorTitle, errorDescription } = props;
  const dispatch = useAppDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);

  const closeTile = () => {
    dispatch(removeTile(tileId));
  };

  const sendHelpRequest = () => {
    dispatch(
      sendHelpRequestThunk({
        config: 'general',
        details: JSON.stringify({ error: errorCode, quoteId }),
      }),
    );

    closeModal();
  };

  return (
    <>
      <VStack
        fullWidth
        fullHeight
        verticalAlignment="center"
        horizontalAlignment="center"
        gap="8px"
        className="w-100 h-100 p-4 text-center bg-lvl1 text-danger"
        data-e2e="tile-error-message"
      >
        <Button onClick={closeTile} purpose="flat" className="position-absolute mt-4 me-3" style={{ top: 0, right: 0 }}>
          <Icon icon="close" size="sm" className="text-secondary" />
        </Button>
        <i className="icon icon-xl">error_outline</i>

        <div className="fw-bold" data-e2e="error-tile-title">
          <FormattedMessage id={isDefined(errorTitle) ? errorTitle : 'error.message.title'} />
        </div>
        <div data-e2e="error-tile-description">
          <FormattedMessage id={isDefined(errorDescription) ? errorDescription : 'error.message.description'} />
        </div>

        <div className="my-3" />

        {isDefined(onReset) && (
          <Button purpose="discreet" onClick={onReset} data-e2e="reset-tile-btn">
            <FormattedMessage id="tile.error.reset" />
          </Button>
        )}

        {isDefined(errorCode) && (
          <UsertypeGuard allow={['Internal Sales', 'Internal Trader']}>
            <Button purpose="primary" color="danger" onClick={openModal} data-e2e="show-error-detail-btn">
              <FormattedMessage id="tile.error.viewSGLog" />
            </Button>
          </UsertypeGuard>
        )}
      </VStack>

      <Modal show={isModalOpen} onHide={closeModal} centered contentClassName="border-danger" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FormattedMessage id="tile.error.SGLogModal.tile" />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column">
          <div className="row flex-nowrap mb-3">
            <aside className="col-4 fw-bold">
              <FormattedMessage id="tile.error.SGLogModal.errorCode" />
            </aside>
            <div className="col-8">{errorCode}</div>
          </div>

          {isDefined(quoteId) && (
            <div className="row flex-nowrap mb-3">
              <aside className="col-4 fw-bold">
                <FormattedMessage id="tile.error.SGLogModal.quoteId" />
              </aside>
              <div className="col-8">{quoteId}</div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button purpose="primary" onClick={sendHelpRequest}>
            <FormattedMessage id="tile.error.sendHelpRequest" />
          </Button>
          <Button purpose="primary" onClick={closeModal}>
            <FormattedMessage id="tile.error.close" />
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
